import React, { useMemo } from "react";
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import "./Modal.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend
);


const FlightChart = ({ data, selectedFlight }) => {
  const chartData = useMemo(() => {
    let filteredData = data.filter(
      (flight) => flight.airports === selectedFlight.airports
    );

    // Grupowanie cen - zliczanie wystąpień
    const priceCount = {};
    filteredData.forEach((flight) => {
      const price = flight.price;
      priceCount[price] = (priceCount[price] || 0) + 1;
    });

    // Konwersja na punkty wykresu - każdy punkt to cena i jej liczebność
    const scatterPoints = Object.entries(priceCount).map(([price, count]) => ({
      x: Math.random() * 100, // losowa pozycja na osi X dla estetyki
      y: parseFloat(price),
      count: count
    }));

    return {
      datasets: [
        {
          label: "Rozkład cen",
          data: scatterPoints,
          backgroundColor: (context) => {
            const count = context.raw?.count || 1;
            const alpha = Math.min(0.3 + (count * 0.1), 0.9);
            return `rgba(102, 126, 234, ${alpha})`;
          },
          borderColor: '#667eea',
          borderWidth: 2,
          pointRadius: (context) => {
            // Im więcej wystąpień, tym większa kropka (min 5, max 25)
            const count = context.raw?.count || 1;
            return Math.min(5 + (count * 2), 25);
          },
          pointHoverRadius: (context) => {
            const count = context.raw?.count || 1;
            return Math.min(7 + (count * 2), 27);
          },
          pointHoverBackgroundColor: '#764ba2',
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 3,
        },
      ],
    };
  }, [data, selectedFlight.airports]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 500,
        easing: 'easeInOutQuart',
      },
      interaction: {
        mode: 'nearest',
        intersect: true,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: '#667eea',
          borderWidth: 1,
          padding: 12,
          displayColors: false,
          callbacks: {
            title: function () {
              return 'Cena';
            },
            label: function (context) {
              const count = context.raw?.count || 1;
              return [
                `${context.parsed.y} zł`,
                `Wystąpień: ${count}x`
              ];
            }
          }
        },
      },
      scales: {
        x: {
          display: false,
          min: 0,
          max: 100,
        },
        y: {
          beginAtZero: false,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
          ticks: {
            callback: function (value) {
              return value + ' zł';
            },
            font: {
              size: 12,
            }
          },
          title: {
            display: true,
            text: 'Cena',
            font: {
              size: 13,
              weight: 'bold'
            }
          }
        },
      },
    }),
    []
  );

  return (
    <div style={{ width: "100%", height: "350px", padding: '1rem 0' }}>
      <Scatter data={chartData} options={options} />
    </div>
  );
};

const FlightDetails = ({ data, selectedFlight }) => {
  const filterData = useMemo(
    () => data.filter((flight) => flight.airports === selectedFlight.airports),
    [data, selectedFlight.airports]
  );

  const minPrice = useMemo(
    () => filterData.length > 0 ? Math.min(...filterData.map(f => f.price)) : 0,
    [filterData]
  );

  const maxPrice = useMemo(
    () => filterData.length > 0 ? Math.max(...filterData.map(f => f.price)) : 0,
    [filterData]
  );

  const avgPrice = useMemo(
    () => filterData.length > 0
      ? Math.round(filterData.reduce((sum, f) => sum + f.price, 0) / filterData.length)
      : 0,
    [filterData]
  );

  const priceDiffFromAvg = useMemo(() => {
    if (filterData.length === 0) return null;
    const currentPrice = selectedFlight?.price || 0;
    const average = Math.round(filterData.reduce((sum, f) => sum + f.price, 0) / filterData.length);
    const diff = currentPrice - average;
    const diffPercent = average > 0 ? ((diff / average) * 100).toFixed(1) : 0;
    return { diff, diffPercent, isHigher: diff > 0, average };
  }, [filterData, selectedFlight?.price]);

  return (
    <div className="flight-details-modal">
      <div className="modal-header-section">
        <div className="destination-info">
          <h2 className="destination-name">{selectedFlight?.name}</h2>
          <p className="route-info">
            {selectedFlight?.country} • {selectedFlight?.airports}
          </p>
        </div>

        <div className="current-price-badge">
          <span className="badge-label">Aktualna cena</span>
          <span className="badge-price">{selectedFlight?.price} zł</span>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card stat-min">
          <div className="stat-icon">📉</div>
          <div className="stat-content">
            <span className="stat-label">Najniższa</span>
            <span className="stat-value">{minPrice} zł</span>
          </div>
        </div>

        <div className="stat-card stat-avg">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <span className="stat-label">Średnia</span>
            <span className="stat-value">{avgPrice} zł</span>
          </div>
        </div>

        <div className="stat-card stat-max">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <span className="stat-label">Najwyższa</span>
            <span className="stat-value">{maxPrice} zł</span>
          </div>
        </div>

        {priceDiffFromAvg && (
          <div className={`stat-card stat-trend ${priceDiffFromAvg.isHigher ? 'trend-up' : 'trend-down'}`}>
            <div className="stat-icon">{priceDiffFromAvg.isHigher ? '📈' : '📉'}</div>
            <div className="stat-content">
              <span className="stat-label">Różnica od średniej</span>
              <span className="stat-value">
                {priceDiffFromAvg.isHigher ? '+' : ''}{priceDiffFromAvg.diff} zł
              </span>
              <div className="stat-subvalue">
                ({priceDiffFromAvg.diffPercent}%)
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="chart-section">
        <h3 className="chart-title">Rozkład cen</h3>
        <p className="chart-subtitle">Wielkość kropki = częstość występowania ceny</p>
        <FlightChart data={data} selectedFlight={selectedFlight} />
      </div>

      <div className="data-info">
        <span className="data-points">📊 {filterData.length} punktów danych</span>
      </div>
    </div>
  );
};

export default FlightDetails;
